import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Spinner, Table, TableHeader, TableColumn, TableBody, TableRow, TableCell, Chip, Button, Switch } from "@heroui/react";
import { Envelope, Paperclip, X, ArrowRight, ArrowLeft, CheckCircle, Chats, ArrowBendUpLeft } from "@phosphor-icons/react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { useSession } from "@clerk/clerk-react";
import { toast } from "react-toastify";
// Gedeeld component: dezelfde bijlageweergave in helpdesk, verzekeringen en
// renovatietickets (het endpoint volgt uit `source`).
import { AttachmentViewer } from "shared/components/email/AttachmentViewer";
import { InitialResponseModal } from "./InitialResponseModal";
import { TranslatePreview } from "shared/components/TranslatePreview";
import { getTicketSupplierAssignments } from "../../shared/ticketSuppliers";

export interface EmailAttachment {
  id: string;
  name: string;
  size: number;
  contentType: string;
  sharePointFileId?: string;
  sharePointFileUrl?: string;
}

export interface Email {
  id: number;
  messageId: string;
  subject: string;
  body: string;
  from: string;
  receivedDateTime: string;
  direction: 'sent' | 'received';
  isRead: boolean;
  hasAttachments?: boolean;
  attachments?: EmailAttachment[];
  noReplyNeeded?: boolean;
  metadata?: {
    to?: string;
    supplierCommunication?: boolean;
  };
}

interface EmailsSectionProps {
  ticket: any;
  communicationMode?: 'tenant' | 'supplier';
  /**
   * In leverancier-modus: toon enkel de correspondentie van deze leverancier
   * (ContactCompany-id), of `'all'` voor alle leveranciers. E-mails waarvan het
   * adres bij géén enkele aan het ticket gekoppelde leverancier hoort, blijven
   * onder elke leverancier zichtbaar zodat er niets verloren gaat.
   */
  selectedSupplierId?: number | 'all';
  isInsurance?: boolean;
  insuranceIssueId?: number;
  onNewEmail?: () => void;
  onRefreshRequest?: (refreshFn: () => Promise<void>) => void;
  onNavigateToMassmail?: (content: string, subject: string) => void;
  onEmailsLoaded?: (emails: Email[]) => void;
  onReply?: (subject: string, body: string, messageId: string, from: string, date: string) => void;
}

export const EmailsSection: React.FC<EmailsSectionProps> = ({
  ticket,
  communicationMode = 'tenant',
  selectedSupplierId = 'all',
  isInsurance = false,
  insuranceIssueId,
  onNewEmail,
  onRefreshRequest,
  onNavigateToMassmail,
  onEmailsLoaded,
  onReply
}) => {
  // Alle e-mails van het ticket, ongefilterd. De zichtbare lijst (`emails`)
  // wordt hieruit afgeleid o.b.v. communicatiemodus en gekozen leverancier.
  const [rawEmails, setRawEmails] = useState<Email[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<Email | null>(null);
  const [isLoadingAttachments, setIsLoadingAttachments] = useState<Set<string>>(new Set());
  const [processedEmailBodies, setProcessedEmailBodies] = useState<Map<string, string>>(new Map());
  const [inlineImageCache, setInlineImageCache] = useState<Map<string, string>>(new Map());
  const [showInitialResponseModal, setShowInitialResponseModal] = useState(false);
  const { session } = useSession();

  // Bepaalt of een e-mail leverancier-correspondentie is (vs. huurder). Zelfde
  // heuristiek als voorheen: relay-adres, HS-onderwerp of expliciete metadata-vlag.
  const isSupplierEmail = useCallback(
    (email: Email): boolean =>
      !!email.from?.includes('repairs@yally.be') ||
      !!email.metadata?.to?.includes('repairs@yally.be') ||
      !!email.subject?.includes(`HS${ticket?.id}`) ||
      email.metadata?.supplierCommunication === true,
    [ticket?.id],
  );

  // Zichtbare e-mails: eerst de tenant/leverancier-splitsing, daarna — in
  // leverancier-modus met een specifiek gekozen leverancier — enkel de mails van
  // díé leverancier plus de "wees"-mails (adres hoort bij geen enkele gekoppelde
  // leverancier), zodat losse correspondentie nooit verdwijnt.
  const emails = useMemo<Email[]>(() => {
    if (isInsurance || !communicationMode) return rawEmails;

    if (communicationMode === 'tenant') {
      return rawEmails.filter((e) => !isSupplierEmail(e));
    }

    const supplierEmails = rawEmails.filter(isSupplierEmail);
    if (selectedSupplierId === 'all') return supplierEmails;

    const assignments = getTicketSupplierAssignments(ticket);
    // Per leverancier tellen ZOWEL het algemene firma-adres als dat van de
    // gekozen contactpersoon mee. Anders verdwijnt het verloop zodra je een
    // contactpersoon instelt: oudere mails staan op `info@…`, nieuwe op het
    // adres van die persoon, en één van de twee zou dan als wees-mail eindigen.
    const addressesOf = (assignment: (typeof assignments)[number]): string[] =>
      [assignment.supplier?.email, assignment.contact?.email]
        .map((addr) => addr?.trim().toLowerCase())
        .filter((addr): addr is string => !!addr);

    const linkedAddresses = assignments.flatMap(addressesOf);
    const selectedAssignment = assignments.find(
      (a) => a.supplier?.id === selectedSupplierId,
    );
    const selectedAddresses = selectedAssignment
      ? addressesOf(selectedAssignment)
      : [];

    const haystack = (e: Email) =>
      `${e.from ?? ''} ${e.metadata?.to ?? ''}`.toLowerCase();

    return supplierEmails.filter((e) => {
      const hay = haystack(e);
      const belongsToAnyLinked = linkedAddresses.some((addr) => hay.includes(addr));
      // Wees-mail (bij geen enkele gekoppelde leverancier) → altijd tonen.
      if (!belongsToAnyLinked) return true;
      // Anders enkel tonen als het adres bij de gekozen leverancier hoort.
      return selectedAddresses.some((addr) => hay.includes(addr));
    });
  }, [rawEmails, communicationMode, selectedSupplierId, isInsurance, isSupplierEmail, ticket]);

  const isRecentIncoming = (email: Email) => {
    const emailDate = new Date(email.receivedDateTime);
    const now = new Date();
    const diffHours = (now.getTime() - emailDate.getTime()) / (1000 * 60 * 60);
    return email.direction === 'received' && diffHours <= 24;
  };

  // Load inline images with authentication
  const loadInlineImageWithAuth = async (emailType: string, messageId: string, contentId: string): Promise<string> => {
    const cacheKey = `${emailType}-${messageId}-${contentId}`;
    
    // Check if already cached
    if (inlineImageCache.has(cacheKey)) {
      return inlineImageCache.get(cacheKey)!;
    }
    
    try {
      const encodedContentId = encodeURIComponent(contentId);
      const response = await fetchWithAuth(
        `${API_URL}/email/inline-attachment/${emailType}/${messageId}/${encodedContentId}`,
        { method: 'GET' },
        session
      );
      
      if (!response.ok) {
        throw new Error(`Failed to load inline image: ${response.status}`);
      }
      
      const blob = await response.blob();
      const dataUrl = URL.createObjectURL(blob);
      
      // Cache the result
      setInlineImageCache(prev => new Map(prev).set(cacheKey, dataUrl));
      
      console.log(`✅ Loaded inline image: ${contentId}`);
      return dataUrl;
    } catch (error) {
      console.error(`❌ Failed to load inline image: ${contentId}`, error);
      // Return a placeholder for missing images
      return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHZpZXdCb3g9IjAgMCAyMCAyMCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjIwIiBoZWlnaHQ9IjIwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0xMCA2TDEwIDEwTDE0IDEwTDE0IDZIMTBaIiBmaWxsPSIjOUI5QjlCIi8+CjxwYXRoIGQ9Ik02IDEwTDYgMTRMMTAgMTRMMTAgMTBINloiIGZpbGw9IiM5QjlCOUIiLz4KPC9zdmc+';
    }
  };

  // Replace cid: URLs in email body with authenticated data URLs
  const replaceCidUrls = async (htmlContent: string, messageId: string): Promise<string> => {
    if (!htmlContent) return htmlContent;
    
    console.log(`🔍 Checking email HTML for CID images (first 500 chars):`, htmlContent.substring(0, 500));
    
    // Determine email type based on context
    let emailType = 'helpdesk';
    if (isInsurance) {
      emailType = 'insurance';
    }
    
    let processedContent = htmlContent;
    const cidMatches: Array<{ match: string; contentId: string; type: 'src' | 'background' | 'style' }> = [];
    
    // Find all CID references in src attributes
    const cidSrcPattern = /src=["']cid:([^"']+)["']/gi;
    let match;
    while ((match = cidSrcPattern.exec(htmlContent)) !== null) {
      console.log(`🎯 Found CID src:`, match[0]);
      cidMatches.push({ match: match[0], contentId: match[1], type: 'src' });
    }
    
    // Find CID references in background-image CSS
    const cidBgPattern = /background-image:\s*url\(["']?cid:([^"')]+)["']?\)/gi;
    while ((match = cidBgPattern.exec(htmlContent)) !== null) {
      console.log(`🎯 Found CID background:`, match[0]);
      cidMatches.push({ match: match[0], contentId: match[1], type: 'background' });
    }
    
    // Find CID references in style attributes
    const cidStyleBgPattern = /style=["']([^"']*background-image:\s*url\(["']?cid:([^"')]+)["']?\)[^"']*)["']/gi;
    while ((match = cidStyleBgPattern.exec(htmlContent)) !== null) {
      console.log(`🎯 Found CID in style:`, match[0]);
      cidMatches.push({ match: match[0], contentId: match[2], type: 'style' });
    }
    
    console.log(`📊 Total CID matches found: ${cidMatches.length}`);
    
    // Load all images and replace URLs
    for (const cidMatch of cidMatches) {
      try {
        const dataUrl = await loadInlineImageWithAuth(emailType, messageId, cidMatch.contentId);
        
        switch (cidMatch.type) {
          case 'src':
            processedContent = processedContent.replace(cidMatch.match, `src="${dataUrl}"`);
            console.log(`🔄 Replaced CID src: cid:${cidMatch.contentId}`);
            break;
          case 'background':
            processedContent = processedContent.replace(cidMatch.match, `background-image: url("${dataUrl}")`);
            console.log(`🔄 Replaced CID background: cid:${cidMatch.contentId}`);
            break;
          case 'style':
            const styleMatch = cidMatch.match.match(/style=["']([^"']*)/);
            if (styleMatch) {
              const updatedStyle = styleMatch[1].replace(/url\(["']?cid:[^"')]+["']?\)/, `url("${dataUrl}")`);
              processedContent = processedContent.replace(cidMatch.match, `style="${updatedStyle}"`);
              console.log(`🔄 Replaced CID in style: cid:${cidMatch.contentId}`);
            }
            break;
        }
      } catch (error) {
        console.error(`❌ Failed to replace CID: ${cidMatch.contentId}`, error);
      }
    }
    
    return processedContent;
  };

  // Process email body to replace CID URLs
  const processEmailBody = async (email: Email) => {
    const cacheKey = email.messageId;
    
    // Skip if already processed
    if (processedEmailBodies.has(cacheKey)) {
      return;
    }
    
    try {
      console.log(`🖼️ Processing email body for images: ${email.messageId}`);
      const processedBody = await replaceCidUrls(email.body, email.messageId);
      setProcessedEmailBodies(prev => new Map(prev).set(cacheKey, processedBody));
    } catch (error) {
      console.error('Failed to process email body:', error);
      // Fallback to original body
      setProcessedEmailBodies(prev => new Map(prev).set(cacheKey, email.body));
    }
  };

  const fetchEmails = useCallback(async () => {
    setIsLoading(true);
    try {
      const endpoint = isInsurance 
        ? `${API_URL}/insurance/file/${insuranceIssueId}/emails`
        : `${API_URL}/helpdesk/ticket/${ticket.id}/emails`;
      
      const response = await fetchWithAuth(endpoint, { method: "GET" }, session);
      
      if (!response.ok) {
        throw new Error('Failed to fetch emails');
      }
      
      const data = await response.json();

      // Sla alle e-mails ongefilterd op. De zichtbare lijst (`emails`) en de
      // auto-selectie worden hieruit afgeleid, zodat wisselen van modus of
      // leverancier geen nieuwe fetch vereist.
      setRawEmails(data);

      // Mark emails as read if there are unread ones
      const hasUnreadEmails = data.some((email: Email) => !email.isRead && email.direction === 'received');
      if (hasUnreadEmails) {
        try {
          const markReadEndpoint = isInsurance
            ? `${API_URL}/insurance/file/${insuranceIssueId}/emails/mark-read`
            : `${API_URL}/helpdesk/ticket/${ticket.id}/emails/mark-read`;
          
          await fetchWithAuth(markReadEndpoint, { method: 'PUT' }, session);
        } catch (error) {
          console.error('Error marking emails as read:', error);
        }
      }
    } catch (error) {
      console.error('Error fetching emails:', error);
      toast.error('Fout bij laden van emails');
    } finally {
      setIsLoading(false);
    }
  }, [ticket?.id, isInsurance, insuranceIssueId, session]);

  const fetchEmailAttachments = async (email: Email) => {
    if (!email.hasAttachments || (email.attachments && email.attachments.length > 0)) return;
    
    try {
      setIsLoadingAttachments(prev => new Set(prev).add(email.messageId));
      
      let emailType = 'acquisitions';
      if (isInsurance) {
        emailType = 'insurance';
      } else if (ticket && !isInsurance) {
        emailType = 'helpdesk';
      }
      
      const response = await fetchWithAuth(
        `${API_URL}/email/message-details/${emailType}/${email.messageId}`,
        { method: "GET" },
        session
      );
      
      if (!response.ok) {
        throw new Error('Failed to fetch email attachments');
      }
      
      const data = await response.json();
      
      setRawEmails(prevEmails =>
        prevEmails.map(e =>
          e.messageId === email.messageId
            ? { ...e, attachments: data.attachments || [] }
            : e
        )
      );
    } catch (error) {
      console.error('Error fetching email attachments:', error);
      toast.error('Fout bij laden van bijlagen');
    } finally {
      setIsLoadingAttachments(prev => {
        const newSet = new Set(prev);
        newSet.delete(email.messageId);
        return newSet;
      });
    }
  };

  const handleDownloadAttachment = async (attachment: EmailAttachment, email: Email) => {
    try {
      let response: Response;
      
      if (!isInsurance && ticket) {
        response = await fetchWithAuth(
          `${API_URL}/email/helpdesk-attachment/${email.messageId}/${attachment.sharePointFileId}`,
          { method: "GET" },
          session
        );
      } else if (isInsurance) {
        response = await fetchWithAuth(
          `${API_URL}/email/insurance-attachment/${email.messageId}/${attachment.sharePointFileId}`,
          { method: "GET" },
          session
        );
      } else {
        if (attachment.sharePointFileUrl && !attachment.sharePointFileUrl.startsWith('#')) {
          response = await fetch(attachment.sharePointFileUrl);
        } else {
          response = await fetchWithAuth(
            `${API_URL}/email/attachment/${email.messageId}/${attachment.sharePointFileId}`,
            { method: "GET" },
            session
          );
        }
      }
      
      if (!response.ok) throw new Error('Download failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = attachment.name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      toast.error('Fout bij downloaden van bijlage');
    }
  };


  const handleEmailRowClick = (email: Email) => {
    setSelectedEmail(email);
    // Processing will be handled by the useEffect below
  };

  const handleToggleNoReplyNeeded = async (emailId: number, currentValue: boolean) => {
    try {
      const endpoint = currentValue
        ? `${API_URL}/email/unmark-no-reply-needed/${emailId}`
        : `${API_URL}/email/mark-no-reply-needed/${emailId}`;

      const response = await fetchWithAuth(endpoint, { method: 'POST' }, session);

      if (!response.ok) {
        throw new Error('Failed to update email status');
      }

      // Update local state
      setRawEmails(prevEmails =>
        prevEmails.map(e =>
          e.id === emailId ? { ...e, noReplyNeeded: !currentValue } : e
        )
      );

      // Update selected email if it's the one being changed
      if (selectedEmail && selectedEmail.id === emailId) {
        setSelectedEmail({ ...selectedEmail, noReplyNeeded: !currentValue });
      }

      toast.success(
        currentValue
          ? 'E-mail gemarkeerd als antwoord nodig'
          : 'E-mail gemarkeerd als geen antwoord nodig'
      );
    } catch (error) {
      console.error('Error toggling no reply needed:', error);
      toast.error('Fout bij bijwerken van e-mail status');
    }
  };

  const handleToggleDirection = async (emailId: number, currentDirection: 'sent' | 'received') => {
    try {
      const newDirection = currentDirection === 'sent' ? 'received' : 'sent';
      const response = await fetchWithAuth(
        `${API_URL}/email/update-direction/${emailId}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ direction: newDirection }),
        },
        session
      );

      if (!response.ok) {
        throw new Error('Failed to update email direction');
      }

      // Update local state
      setRawEmails(prevEmails =>
        prevEmails.map(e =>
          e.id === emailId ? { ...e, direction: newDirection } : e
        )
      );

      // Update selected email if it's the one being changed
      if (selectedEmail && selectedEmail.id === emailId) {
        setSelectedEmail({ ...selectedEmail, direction: newDirection });
      }

      toast.success(
        `E-mail richting gewijzigd naar ${newDirection === 'sent' ? 'Verzonden' : 'Ontvangen'}`
      );
    } catch (error) {
      console.error('Error toggling email direction:', error);
      toast.error('Fout bij bijwerken van e-mail richting');
    }
  };

  // Process selected email (both for auto-selected and manually selected emails)
  useEffect(() => {
    if (selectedEmail) {
      // Process email body for inline images
      processEmailBody(selectedEmail);
      // Fetch attachments if needed
      if (selectedEmail.hasAttachments && (!selectedEmail.attachments || selectedEmail.attachments.length === 0)) {
        fetchEmailAttachments(selectedEmail);
      }
    }
  }, [selectedEmail?.messageId]); // Only trigger when messageId changes to avoid loops

  useEffect(() => {
    if (ticket?.id) {
      fetchEmails();
    }
  }, [ticket?.id, communicationMode]);

  // Houd de parent-notificatie en de geselecteerde e-mail in sync met de
  // zichtbare lijst. Als de huidige selectie wegvalt (bv. na wisselen van
  // leverancier) selecteren we automatisch de meest recente inkomende e-mail.
  useEffect(() => {
    if (onEmailsLoaded) onEmailsLoaded(emails);
    setSelectedEmail((prev) => {
      if (prev && emails.some((e) => e.id === prev.id)) return prev;
      const recentIncoming = [...emails]
        .filter((e) => e.direction === 'received')
        .sort(
          (a, b) =>
            new Date(b.receivedDateTime).getTime() -
            new Date(a.receivedDateTime).getTime(),
        )[0];
      return recentIncoming ?? null;
    });
  }, [emails, onEmailsLoaded]);

  // Expose fetchEmails to parent component
  useEffect(() => {
    if (onRefreshRequest) {
      onRefreshRequest(fetchEmails);
    }
  }, [onRefreshRequest, fetchEmails]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Spinner size="lg" color="primary" />
        <p className="mt-4 text-neutral-secondary-mid">Emails laden...</p>
      </div>
    );
  }

  if (emails.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 p-4">
        <Envelope className="h-16 w-16 text-neutral-secondary-mid/50 mb-4" />
        <p className="text-lg font-medium text-neutral-primary mb-2">Geen emails gevonden</p>
        <p className="text-sm text-neutral-secondary-mid mb-6">
          Er zijn nog geen emails gekoppeld aan dit {isInsurance ? 'dossier' : 'ticket'}.
        </p>
        {onNewEmail && (
          <Button
            onClick={onNewEmail}
            className="bg-brick-primary text-white hover:bg-brick-primary/90"
            startContent={<div className="h-4 w-4 text-white">+</div>}
          >
            Verstuur eerste email
          </Button>
        )}
      </div>
    );
  }

  return (
    <>
      {/* Email Flow Table - Left Column */}
      <div className="bg-muted border border-muted-foreground/20 rounded-lg">
        <div className="border-b border-neutral-primary/10 px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Envelope size={20} className="text-moss-primary" />
                <h3 className="font-semibold text-neutral-primary">Email verloop ({emails.length})</h3>
              </div>
              <p className="text-xs text-neutral-secondary-mid mt-1">Klik op een rij om details te bekijken</p>
            </div>
            <div className="flex items-center gap-2">
              {onNavigateToMassmail && !isInsurance && communicationMode === 'tenant' && (
                <Button
                  onPress={() => setShowInitialResponseModal(true)}
                  className="bg-blue-600 text-white hover:bg-blue-700"
                  startContent={<Chats size={16} weight="fill" />}
                  size="sm"
                >
                  Initiële antwoord
                </Button>
              )}
              {onNewEmail && (
                <Button
                  onPress={onNewEmail}
                  className="bg-moss-primary text-white hover:bg-moss-primary/90"
                  startContent={<div className="h-4 w-4 text-white">+</div>}
                  size="sm"
                >
                  Verstuur email
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="p-4">
        
        <Table 
          aria-label="Email flow table"
          classNames={{
            wrapper: "shadow-none rounded-sm border border-muted-foreground/20 bg-muted",
            th: "text-foreground font-medium bg-muted-foreground/10",
            td: "py-3"
          }}
        >
          <TableHeader>
            <TableColumn>RICHTING</TableColumn>
            <TableColumn>DATUM/TIJD</TableColumn>
            <TableColumn>VAN</TableColumn>
            <TableColumn>NAAR</TableColumn>
            <TableColumn>ONDERWERP</TableColumn>
            <TableColumn>STATUS</TableColumn>
            <TableColumn>BIJLAGEN</TableColumn>
          </TableHeader>
          <TableBody>
            {emails
              .sort((a, b) => new Date(b.receivedDateTime).getTime() - new Date(a.receivedDateTime).getTime())
              .map((email) => {
                const isSent = email.direction === 'sent';
                const isRecent = isRecentIncoming(email);
                
                return (
                  <TableRow 
                    key={email.id}
                    className={`hover:bg-neutral-primary/5 cursor-pointer group ${
                      selectedEmail && selectedEmail.id === email.id 
                        ? 'bg-moss-primary/10 border-l-4 border-moss-primary' 
                        : ''
                    }`}
                    onClick={() => handleEmailRowClick(email)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {isSent ? (
                          <ArrowRight className="text-moss-primary" size={16} />
                        ) : (
                          <ArrowLeft className="text-blue-600" size={16} />
                        )}
                        <Chip
                          size="sm"
                          className={isSent 
                            ? "bg-moss-primary/10 text-moss-primary" 
                            : "bg-blue-100 text-blue-700"
                          }
                        >
                          {isSent ? 'Verzonden' : 'Ontvangen'}
                        </Chip>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <div className="font-medium">
                          {new Date(email.receivedDateTime).toLocaleDateString('nl-NL')}
                        </div>
                        <div className="text-neutral-secondary-mid">
                          {new Date(email.receivedDateTime).toLocaleTimeString('nl-NL', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium truncate max-w-32">
                        {email.from}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium truncate max-w-32">
                        {email.metadata?.to || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm font-medium truncate max-w-48">
                        {email.subject}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {!email.isRead && !isSent && (
                          <Chip size="sm" className="bg-red-100 text-red-700">
                            Ongelezen
                          </Chip>
                        )}
                        {isRecent && (
                          <Chip size="sm" className="bg-orange-100 text-orange-700">
                            Nieuw
                          </Chip>
                        )}
                        {email.isRead && !isSent && !email.noReplyNeeded && (
                          <Chip size="sm" className="bg-green-100 text-green-700">
                            Gelezen
                          </Chip>
                        )}
                        {email.noReplyNeeded && !isSent && (
                          <Chip size="sm" className="bg-moss-primary/10 text-moss-primary" startContent={<CheckCircle size={12} weight="fill" />}>
                            Geen antwoord nodig
                          </Chip>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {email.hasAttachments && (
                        <div className="flex items-center gap-1">
                          <Paperclip size={14} className="text-neutral-secondary-mid" />
                          <span className="text-sm text-neutral-secondary-mid">
                            {isLoadingAttachments.has(email.messageId) 
                              ? '...' 
                              : (email.attachments?.length ?? '?')
                            }
                          </span>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
        </div>
      </div>

      {/* Expanded Email View - Right Column */}
      <div className="bg-muted border border-muted-foreground/20 rounded-lg flex flex-col">
        <div className="sticky top-0 z-10 bg-muted border-b border-neutral-primary/10 px-4 py-3 rounded-t-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Envelope size={20} className="text-moss-primary" />
              <h3 className="font-semibold text-neutral-primary">Email Details</h3>
            </div>
            <div className="flex items-center gap-2">
              {selectedEmail && selectedEmail.direction === 'received' && onReply && (
                <Button
                  onClick={() => {
                    try {
                      onReply(
                        selectedEmail.subject,
                        selectedEmail.body,
                        selectedEmail.messageId,
                        selectedEmail.from,
                        selectedEmail.receivedDateTime
                      );
                    } catch (error) {
                      console.error('Error triggering reply:', error);
                    }
                  }}
                  size="sm"
                  className="bg-brick-secondary-fine text-brick-primary hover:bg-brick-secondary-fine/80"
                  startContent={<ArrowBendUpLeft size={16} weight="bold" />}
                >
                  Reageer
                </Button>
              )}
              {selectedEmail && (
                <Button
                  onClick={() => setSelectedEmail(null)}
                  isIconOnly
                  variant="light"
                  className="text-neutral-secondary-mid hover:text-brick-primary"
                  size="sm"
                >
                  <X size={18} />
                </Button>
              )}
            </div>
          </div>
        </div>
        {selectedEmail ? (
        <div className="p-6">
          
          {/* Email Header */}
          <div className="mb-4 pb-4 border-b border-neutral-primary/10">
            <h4 className="text-lg font-semibold text-neutral-primary mb-2">
              {selectedEmail.subject}
            </h4>
            <div className="text-sm text-neutral-secondary-mid space-y-1">
              <div><strong>Van:</strong> {selectedEmail.from}</div>
              {selectedEmail.metadata?.to && (
                <div><strong>Naar:</strong> {selectedEmail.metadata.to}</div>
              )}
              <div><strong>Datum:</strong> {new Date(selectedEmail.receivedDateTime).toLocaleString('nl-NL')}</div>
              <div className="flex items-center gap-2 justify-between">
                <div className="flex items-center gap-2">
                  <strong>Richting:</strong>
                  <Chip
                    size="sm"
                    className={selectedEmail.direction === 'sent'
                      ? "bg-moss-primary/10 text-moss-primary"
                      : "bg-blue-100 text-blue-700"
                    }
                  >
                    {selectedEmail.direction === 'sent' ? 'Verzonden' : 'Ontvangen'}
                  </Chip>
                </div>
                <Button
                  size="sm"
                  variant="light"
                  onClick={() => handleToggleDirection(selectedEmail.id, selectedEmail.direction)}
                  startContent={selectedEmail.direction === 'sent' ? <ArrowLeft size={14} /> : <ArrowRight size={14} />}
                  className="text-xs text-neutral-secondary-mid hover:text-moss-primary"
                >
                  Wijzig naar {selectedEmail.direction === 'sent' ? 'Ontvangen' : 'Verzonden'}
                </Button>
              </div>
              {selectedEmail.direction === 'received' && (
                <div className="flex items-center gap-3 pt-2 mt-2 border-t border-neutral-primary/10">
                  <div className="flex items-center gap-2 flex-1">
                    <CheckCircle
                      size={18}
                      className={selectedEmail.noReplyNeeded ? "text-moss-primary" : "text-neutral-secondary-mid"}
                      weight={selectedEmail.noReplyNeeded ? "fill" : "regular"}
                    />
                    <span className="text-sm font-medium text-neutral-primary">
                      Moet niet op geantwoord worden
                    </span>
                  </div>
                  <Switch
                    size="sm"
                    isSelected={selectedEmail.noReplyNeeded || false}
                    onValueChange={() => handleToggleNoReplyNeeded(selectedEmail.id, selectedEmail.noReplyNeeded || false)}
                    classNames={{
                      wrapper: "group-data-[selected=true]:bg-moss-primary"
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Email Body */}
          <div className="mb-6">
            <h5 className="font-medium text-neutral-primary mb-3">Bericht</h5>
            <TranslatePreview
              htmlContent={processedEmailBodies.get(selectedEmail.messageId) || selectedEmail.body}
              bodyClassName="prose prose-sm max-w-none text-neutral-primary bg-white p-4 rounded-sm border max-h-96 overflow-y-auto"
            />
          </div>

          {/* Attachments */}
          {selectedEmail.hasAttachments && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h5 className="font-medium text-neutral-primary text-sm">
                  Bijlagen {selectedEmail.attachments ? `(${selectedEmail.attachments.length})` : ''}
                </h5>
                {!selectedEmail.attachments && (
                  <Button
                    size="sm"
                    variant="flat"
                    onClick={() => fetchEmailAttachments(selectedEmail)}
                    isLoading={isLoadingAttachments.has(selectedEmail.messageId)}
                    className="bg-moss-primary/10 text-moss-primary hover:bg-moss-primary/20"
                    startContent={<Paperclip size={14} />}
                  >
                    Bijlagen laden
                  </Button>
                )}
              </div>
              {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                <div className="space-y-2">
                  {selectedEmail.attachments.map((attachment, index) => (
                    <AttachmentViewer
                      key={`${attachment.name}-${index}`}
                      attachment={attachment}
                      email={selectedEmail}
                      source={isInsurance ? 'insurance' : 'helpdesk'}
                      onDownload={handleDownloadAttachment}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-12 p-4">
            <Envelope className="h-16 w-16 text-neutral-secondary-mid/50 mb-4" />
            <p className="text-sm text-neutral-secondary-mid text-center">
              Selecteer een email uit de lijst om details te bekijken
            </p>
          </div>
        )}
      </div>

      {/* Initial Response Modal */}
      {onNavigateToMassmail && (
        <InitialResponseModal
          isOpen={showInitialResponseModal}
          onClose={() => setShowInitialResponseModal(false)}
          ticket={ticket}
          onNavigateToMassmail={onNavigateToMassmail}
        />
      )}
    </>
  );
};
